import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Calendar, Heart } from 'lucide-react';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom tool icon
const createToolIcon = () => {
  const color = '#ea580c';
  return new L.Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg width="32" height="45" viewBox="0 0 32 45" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.2 0 0 7.2 0 16c0 16 16 29 16 29s16-13 16-29c0-8.8-7.2-16-16-16z" fill="${color}"/>
        <circle cx="16" cy="16" r="8" fill="white"/>
        <circle cx="16" cy="16" r="6" fill="${color}"/>
      </svg>
    `)}`,
    iconSize: [32, 45],
    iconAnchor: [16, 45],
    popupAnchor: [0, -45],
  });
};

// Compute the zoom level appropriate for a given radius (km) in mid-latitudes.
// Approximation: zoom 13 ≈ 1km, then each level halves/doubles the area.
const zoomForRadius = (km) => {
  if (!km || km <= 0) return 11;            // city level
  if (km <= 5) return 12;
  if (km <= 10) return 11;
  if (km <= 25) return 10;
  if (km <= 50) return 9;
  if (km <= 100) return 8;
  if (km <= 200) return 7;
  return 6;                                  // whole country
};

// Center the map on the searched city the FIRST time the search changes
// (not at every marker reload). On homepage (no center), auto-fit bounds once.
const CenterOnSearch = ({ center, radiusKm, tools }) => {
  const map = useMap();
  const lastSearchKey = useRef(null);
  const didFitBoundsRef = useRef(false);

  useEffect(() => {
    if (center && typeof center.lat === 'number' && typeof center.lng === 'number') {
      const key = `${center.lat},${center.lng},${radiusKm || 0}`;
      // Only recenter when the search itself changes (city/radius),
      // NOT when tools are reloaded by zoom/pan.
      if (lastSearchKey.current !== key) {
        lastSearchKey.current = key;
        map.setView([center.lat, center.lng], zoomForRadius(radiusKm), { animate: true });
      }
    } else {
      lastSearchKey.current = null;
      // No city searched: fit on all markers ONCE (homepage initial load)
      if (!didFitBoundsRef.current && tools && tools.length >= 2) {
        didFitBoundsRef.current = true;
        const bounds = L.latLngBounds(tools.map(t => [t.latitude, t.longitude]));
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }
  }, [center, radiusKm, tools, map]);
  return null;
};

// Notify the parent of viewport changes (debounced) so it can reload the markers
// for the visible area while preserving the rest of the filters.
const BoundsWatcher = ({ onBoundsChange }) => {
  const debounceRef = useRef(null);

  const emitBounds = (map) => {
    if (!onBoundsChange) return;
    const c = map.getCenter();
    const b = map.getBounds();
    // Radius in km from center to the NE corner (covers the visible area)
    const distanceMeters = c.distanceTo(b.getNorthEast());
    const radiusKm = Math.max(1, Math.round(distanceMeters / 1000));
    onBoundsChange(c.lat, c.lng, radiusKm);
  };

  const handleViewportChange = (e) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => emitBounds(e.target), 350);
  };

  useMapEvents({
    moveend: handleViewportChange,
    zoomend: handleViewportChange,
  });

  return null;
};

const ToolsMap = ({ tools, onToolSelect, onBooking, initialCenter, initialRadiusKm, onBoundsChange, currentUser, favorites = [], onToggleFavorite }) => {
  const defaultCenter = initialCenter && initialCenter.lat && initialCenter.lng
    ? [initialCenter.lat, initialCenter.lng]
    : [42.5, 12.5];
  const defaultZoom = initialCenter ? zoomForRadius(initialRadiusKm) : 6;

  const handleMarkerClick = (tool) => {
    if (onToolSelect) onToolSelect(tool);
  };

  const handleBookingClick = (tool, e) => {
    e.stopPropagation();
    e.preventDefault();
    onBooking(tool);
  };

  return (
    <div className="w-full h-full">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg z-0"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <CenterOnSearch center={initialCenter} radiusKm={initialRadiusKm} tools={tools} />
        <BoundsWatcher onBoundsChange={onBoundsChange} />

        {tools.map((tool) => (
          <Marker
            key={tool.id}
            position={[tool.latitude, tool.longitude]}
            icon={createToolIcon()}
            eventHandlers={{
              click: () => handleMarkerClick(tool)
            }}
          >
            <Popup className="tool-popup" minWidth={300}>
              <div className="p-2 min-w-[280px] relative">
                {currentUser && onToggleFavorite && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleFavorite(tool.id); }}
                    className="absolute top-0 right-0 z-10 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-md transition-transform hover:scale-110"
                    title={favorites.includes(tool.id) ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                    data-testid={`marker-favorite-${tool.id}`}
                  >
                    <Heart
                      className={`h-4 w-4 ${favorites.includes(tool.id) ? 'fill-red-500 text-red-500' : 'text-slate-400'}`}
                    />
                  </button>
                )}
                <div className="flex items-start justify-between mb-2 pr-8">
                  <h3 className="font-semibold text-base text-slate-900 line-clamp-1">
                    {tool.title ? tool.title.split(' ').map(word =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ').replace(' - ', ' - ') : 'Piattaforma'}
                  </h3>
                </div>

                <div className="flex items-center text-slate-600 text-xs mb-2">
                  <MapPin className="h-3 w-3 mr-1" />
                  {tool.address && tool.civic_number
                    ? `${tool.address} ${tool.civic_number}, ${tool.city || tool.location}`
                    : tool.location}
                </div>

                {tool.reviews_count > 0 ? (
                  <div className="flex items-center gap-1 text-xs mb-2" data-testid="marker-rating">
                    <span className="text-yellow-500">★</span>
                    <span className="font-semibold text-slate-700">{tool.average_rating?.toFixed(1)}</span>
                    <span className="text-slate-400">({tool.reviews_count})</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
                    <span className="text-slate-300">★</span>
                    <span>Nessuna recensione</span>
                  </div>
                )}

                {tool.images && tool.images[0] && (
                  <div className="w-full h-28 bg-gradient-to-b from-slate-50 to-slate-100 rounded-lg mb-2 flex items-center justify-center p-2">
                    <img
                      src={tool.images[0]}
                      alt={`Piattaforma aerea ${(() => { const t = (tool.title || '').split(' - ')[0].replace(/_/g, ' '); return t.charAt(0).toUpperCase() + t.slice(1); })()}${tool.max_height ? ` ${tool.max_height} m` : ''}${tool.city ? ` a ${tool.city}` : ''}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}

                {tool.description && (
                  <p className="text-slate-600 text-xs mb-2 line-clamp-1">
                    {tool.description}
                  </p>
                )}

                {tool.rental_type && (
                  <div className="text-slate-600 text-xs mb-2">
                    {tool.rental_type === 'freddo' ? 'Noleggio a Freddo' :
                     tool.rental_type === 'caldo' ? 'Noleggio a Caldo' :
                     tool.rental_type === 'freddo_caldo' ? 'Noleggio a Freddo e Caldo' :
                     tool.rental_type}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-1 mb-2 text-xs">
                  <div className="text-center p-1 bg-slate-50 rounded">
                    <div className="text-slate-500">Giorno</div>
                    <div className="font-semibold">€{tool.daily_rate?.toFixed(0) || 'N/D'}</div>
                  </div>
                  <div className="text-center p-1 bg-slate-50 rounded">
                    <div className="text-slate-500">Settimana</div>
                    <div className="font-semibold">€{tool.weekly_rate?.toFixed(0) || 'N/D'}</div>
                  </div>
                  <div className="text-center p-1 bg-slate-50 rounded">
                    <div className="text-slate-500">Mese</div>
                    <div className="font-semibold">€{tool.monthly_rate?.toFixed(0) || 'N/D'}</div>
                  </div>
                </div>

                <p className="text-[10px] text-slate-500 italic mb-2 leading-tight">
                  Le immagini sono indicative della categoria dell'attrezzatura. Verifica sempre nell'annuncio le caratteristiche effettive del mezzo disponibile.
                </p>

                <div className="flex items-center justify-between">
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    Disponibile
                  </Badge>
                  <Button
                    type="button"
                    onClick={(e) => handleBookingClick(tool, e)}
                    size="sm"
                    className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white relative z-50 text-xs h-7"
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Prenota
                  </Button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default ToolsMap;
